import { useState, useCallback, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Upload,
  QrCode,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  Ban,
  CheckCircle2,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AssetQRLabel } from "@/components/assets/asset-qr-label";
import { CsvImportPreview } from "@/components/assets/csv-import-preview";
import { AssetUtilizationChart } from "@/components/assets/asset-utilization-chart";
import {
  ASSET_TYPES,
  ASSET_STATUSES,
  type CreateAssetInput,
} from "@pph-safety/shared";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/admin/assets")({
  component: AdminAssetsPage,
});

interface Asset {
  id: string;
  name: string;
  assetType: string;
  serialNumber: string | null;
  tagNumber: string | null;
  location: string | null;
  status: string;
  isActive: boolean;
  inspectionIntervalDays: number | null;
  inspectionTemplateId: string | null;
  inspectionTemplateName?: string | null;
  imageFileId: string | null;
  notes: string | null;
  lastInspectedAt: string | null;
  nextInspectionDueAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OverdueAsset {
  id: string;
  name: string;
  assetType: string;
  daysOverdue: number;
  lastInspectedAt: string | null;
  nextInspectionDueAt: string | null;
}

interface InspectionTemplate {
  id: string;
  name: string;
  category: string;
  isActive: boolean;
}

interface UtilizationData {
  summary: {
    totalAssets: number;
    checkedOutNow: number;
    availableNow: number;
    outOfServiceNow: number;
    pctCheckedOut: number;
  };
  topUsers: { userName: string; checkoutCount: number; damagedReturns: number }[];
  idleAssets: { assetId: string; assetName: string; assetType: string; daysSinceLastUse: number }[];
}

const ASSET_TYPE_LABELS: Record<string, string> = {
  all: "All",
  tool: "Tool",
  equipment: "Equipment",
  vehicle: "Vehicle",
  safety_equipment: "Safety Equipment",
  other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
  all: "All",
  available: "Available",
  checked_out: "Checked Out",
  out_of_service: "Out of Service",
  retired: "Retired",
};

const STATUS_COLORS: Record<string, string> = {
  available: "bg-green-100 text-green-800",
  checked_out: "bg-blue-100 text-blue-800",
  out_of_service: "bg-red-100 text-red-800",
  retired: "bg-gray-100 text-gray-600",
};

function AdminAssetsPage() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [detailAsset, setDetailAsset] = useState<Asset | null>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showOverdue, setShowOverdue] = useState(true);
  const [showUtilization, setShowUtilization] = useState(false);
  const [oosDialogAsset, setOosDialogAsset] = useState<Asset | null>(null);
  const [oosReason, setOosReason] = useState("");

  // Date range for utilization report (default: last 30 days)
  const defaultDateFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  }, []);
  const defaultDateTo = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);

  // Debounce search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      const timeout = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      return () => clearTimeout(timeout);
    },
    []
  );

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin",
      "assets",
      typeFilter,
      statusFilter,
      debouncedSearch,
      page,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: "20",
      });
      if (typeFilter !== "all") params.set("assetType", typeFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      return apiGet<{ data: Asset[]; total: number }>(
        `/api/admin/assets?${params}`
      );
    },
  });

  // Overdue inspections query
  const { data: overdueData } = useQuery({
    queryKey: ["admin", "assets", "overdue-inspections"],
    queryFn: () =>
      apiGet<{ data: OverdueAsset[] }>("/api/admin/assets/overdue-inspections"),
  });
  const overdueAssets = overdueData?.data || [];

  // Utilization report query
  const { data: utilizationData, isLoading: utilizationLoading } = useQuery({
    queryKey: ["admin", "assets", "utilization", dateFrom, dateTo],
    queryFn: () =>
      apiGet<{ data: UtilizationData }>(
        `/api/admin/assets/utilization?dateFrom=${dateFrom}T00:00:00.000Z&dateTo=${dateTo}T23:59:59.999Z`
      ),
    enabled: showUtilization,
  });

  const assets = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 20);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/assets/${id}`),
    onSuccess: () => {
      toast.success("Asset deactivated");
      queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const bulkImportMutation = useMutation({
    mutationFn: (rows: CreateAssetInput[]) =>
      apiPost<{ data: Asset[]; count: number }>("/api/admin/assets/bulk-import", {
        rows,
      }),
    onSuccess: (result) => {
      toast.success(`Imported ${result.count} assets`);
      queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
      setShowCsvImport(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // OOS toggle mutation
  const oosToggleMutation = useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: "out_of_service" | "available";
      reason?: string;
    }) =>
      apiPatch<{ data: Asset }>(`/api/admin/assets/${id}/status`, {
        status,
        reason,
      }),
    onSuccess: (_, variables) => {
      toast.success(
        variables.status === "out_of_service"
          ? "Asset marked out of service"
          : "Asset returned to service"
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
      setOosDialogAsset(null);
      setOosReason("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleOosToggle(asset: Asset) {
    if (asset.status === "out_of_service") {
      // Clear OOS -- no reason needed
      if (window.confirm("Return this asset to available status?")) {
        oosToggleMutation.mutate({ id: asset.id, status: "available" });
      }
    } else {
      // Set OOS -- prompt for reason
      setOosDialogAsset(asset);
      setOosReason("");
    }
  }

  function confirmOos() {
    if (!oosDialogAsset || !oosReason.trim()) return;
    oosToggleMutation.mutate({
      id: oosDialogAsset.id,
      status: "out_of_service",
      reason: oosReason.trim(),
    });
  }

  if (isLoading && assets.length === 0) {
    return (
      <div className="space-y-2 py-8">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Asset Registry
        </h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowUtilization(!showUtilization)}
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            Report
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCsvImport(!showCsvImport)}
          >
            <Upload className="h-4 w-4 mr-1" />
            Import
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Overdue Inspections Section */}
      {overdueAssets.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <button
              onClick={() => setShowOverdue(!showOverdue)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  Overdue Inspections
                </span>
                <Badge className="bg-amber-600 text-white text-xs px-1.5 py-0">
                  {overdueAssets.length}
                </Badge>
              </div>
              {showOverdue ? (
                <ChevronUp className="h-4 w-4 text-amber-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-amber-600" />
              )}
            </button>
            {showOverdue && (
              <div className="mt-3 space-y-2">
                {overdueAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between gap-2 text-sm bg-card rounded-lg p-3 border border-amber-200 dark:border-amber-800"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium block truncate">
                        {asset.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {ASSET_TYPE_LABELS[asset.assetType] || asset.assetType}
                        {asset.lastInspectedAt && (
                          <> &middot; Last: {new Date(asset.lastInspectedAt).toLocaleDateString()}</>
                        )}
                      </span>
                    </div>
                    <Badge className="bg-red-500/15 text-red-700 dark:text-red-400 text-xs px-1.5 py-0 shrink-0">
                      {asset.daysOverdue}d overdue
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Utilization Report Section */}
      {showUtilization && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Utilization Report</span>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setShowUtilization(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
            {utilizationLoading ? (
              <div className="space-y-2 py-4">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
              </div>
            ) : utilizationData?.data ? (
              <AssetUtilizationChart data={utilizationData.data} />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Select a date range to view utilization data.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* CSV Import section */}
      {showCsvImport && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">CSV Import</span>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setShowCsvImport(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <CsvImportPreview
              onImport={(rows) => bulkImportMutation.mutate(rows)}
              isLoading={bulkImportMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <div className="space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>

        {/* Type filter chips */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Type:</span>
          {["all", ...ASSET_TYPES].map((type) => (
            <button
              key={type}
              onClick={() => {
                setTypeFilter(type);
                setPage(1);
              }}
              className={cn(
                "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                typeFilter === type
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {ASSET_TYPE_LABELS[type] || type}
            </button>
          ))}
        </div>

        {/* Status filter chips */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Status:</span>
          {["all", ...ASSET_STATUSES].map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setPage(1);
              }}
              className={cn(
                "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                statusFilter === status
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {STATUS_LABELS[status] || status}
            </button>
          ))}
        </div>
      </div>

      {/* Asset list */}
      {assets.length === 0 ? (
        <div className="flex items-center justify-center h-32 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">
            {debouncedSearch || typeFilter !== "all" || statusFilter !== "all"
              ? "No assets match your filters"
              : "No assets yet. Add one or import from CSV."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {assets.map((asset) => (
            <Card
              key={asset.id}
              className="cursor-pointer transition-colors hover:bg-muted/30"
              onClick={() => setDetailAsset(asset)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">
                        {asset.name}
                      </span>
                      <Badge variant="outline" className="text-xs px-1.5 py-0">
                        {ASSET_TYPE_LABELS[asset.assetType] || asset.assetType}
                      </Badge>
                      <Badge
                        className={cn(
                          "text-xs px-1.5 py-0 border-0",
                          STATUS_COLORS[asset.status] || "bg-gray-100"
                        )}
                      >
                        {STATUS_LABELS[asset.status] || asset.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      {asset.serialNumber && <span>SN: {asset.serialNumber}</span>}
                      {asset.tagNumber && <span>Tag: {asset.tagNumber}</span>}
                      {asset.location && <span>{asset.location}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* OOS Toggle button */}
                    {asset.status !== "retired" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className={cn(
                          "h-8 w-8",
                          asset.status === "out_of_service"
                            ? "text-green-600 hover:text-green-700"
                            : "text-red-500 hover:text-red-600"
                        )}
                        title={
                          asset.status === "out_of_service"
                            ? "Clear Out of Service"
                            : "Mark Out of Service"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOosToggle(asset);
                        }}
                      >
                        {asset.status === "out_of_service" ? (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : (
                          <Ban className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingAsset(asset);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Deactivate this asset? It will be marked as retired.")) {
                          deleteMutation.mutate(asset.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages} ({total} assets)
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* OOS Reason Dialog */}
      {oosDialogAsset && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-lg max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-semibold">Mark Out of Service</h3>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{oosDialogAsset.name}</span>{" "}
              will be unavailable for checkout until cleared.
            </p>
            <div>
              <Label className="text-xs">Reason *</Label>
              <textarea
                value={oosReason}
                onChange={(e) => setOosReason(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                placeholder="e.g. Broken blade guard, needs replacement"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setOosDialogAsset(null);
                  setOosReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={!oosReason.trim() || oosToggleMutation.isPending}
                onClick={confirmOos}
              >
                {oosToggleMutation.isPending ? "Saving..." : "Mark OOS"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Sheet */}
      <AssetFormSheet
        open={showCreate || editingAsset !== null}
        asset={editingAsset}
        onClose={() => {
          setShowCreate(false);
          setEditingAsset(null);
        }}
      />

      {/* Detail Sheet */}
      <AssetDetailSheet
        asset={detailAsset}
        onClose={() => setDetailAsset(null)}
        onEdit={(asset) => {
          setDetailAsset(null);
          setEditingAsset(asset);
        }}
        onOosToggle={handleOosToggle}
      />
    </div>
  );
}

// --------------- Asset Form Sheet ---------------

function AssetFormSheet({
  open,
  asset,
  onClose,
}: {
  open: boolean;
  asset: Asset | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = asset !== null;

  const [name, setName] = useState("");
  const [assetType, setAssetType] = useState<string>("tool");
  const [serialNumber, setSerialNumber] = useState("");
  const [tagNumber, setTagNumber] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [inspectionTemplateId, setInspectionTemplateId] = useState("");
  const [inspectionIntervalDays, setInspectionIntervalDays] = useState("");

  // Populate form when asset changes
  const populateForm = useCallback(() => {
    if (asset) {
      setName(asset.name);
      setAssetType(asset.assetType);
      setSerialNumber(asset.serialNumber || "");
      setTagNumber(asset.tagNumber || "");
      setLocation(asset.location || "");
      setNotes(asset.notes || "");
      setInspectionTemplateId(asset.inspectionTemplateId || "");
      setInspectionIntervalDays(
        asset.inspectionIntervalDays ? String(asset.inspectionIntervalDays) : ""
      );
    } else {
      setName("");
      setAssetType("tool");
      setSerialNumber("");
      setTagNumber("");
      setLocation("");
      setNotes("");
      setInspectionTemplateId("");
      setInspectionIntervalDays("");
    }
  }, [asset]);

  // Load inspection templates for the dropdown
  const { data: templatesData } = useQuery({
    queryKey: ["admin", "inspection-templates"],
    queryFn: () =>
      apiGet<{ data: InspectionTemplate[] }>("/api/admin/inspection-templates"),
    enabled: open,
  });
  const templates = templatesData?.data || [];

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost("/api/admin/assets", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
      toast.success("Asset created");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Record<string, unknown>;
    }) => apiPatch(`/api/admin/assets/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
      toast.success("Asset updated");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSubmit() {
    const body: Record<string, unknown> = {
      name: name.trim(),
      assetType,
    };
    if (serialNumber.trim()) body.serialNumber = serialNumber.trim();
    else if (isEdit) body.serialNumber = null;
    if (tagNumber.trim()) body.tagNumber = tagNumber.trim();
    else if (isEdit) body.tagNumber = null;
    if (location.trim()) body.location = location.trim();
    else if (isEdit) body.location = null;
    if (notes.trim()) body.notes = notes.trim();
    else if (isEdit) body.notes = null;
    if (inspectionTemplateId) body.inspectionTemplateId = inspectionTemplateId;
    else if (isEdit) body.inspectionTemplateId = null;
    if (inspectionIntervalDays) {
      body.inspectionIntervalDays = parseInt(inspectionIntervalDays, 10);
    } else if (isEdit) {
      body.inspectionIntervalDays = null;
    }

    if (isEdit) {
      updateMutation.mutate({ id: asset.id, body });
    } else {
      createMutation.mutate(body);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(open) => {
        if (!open) onClose();
        else populateForm();
      }}
    >
      <SheetContent
        side="right"
        className="overflow-y-auto"
        onOpenAutoFocus={() => populateForm()}
      >
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Asset" : "New Asset"}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? "Update asset details below."
              : "Enter the details for the new asset."}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g. Cordless Drill #3"
            />
          </div>
          <div>
            <Label className="text-xs">Asset Type *</Label>
            <select
              value={assetType}
              onChange={(e) => setAssetType(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              {ASSET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ASSET_TYPE_LABELS[t] || t}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Serial Number</Label>
              <Input
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Tag Number</Label>
              <Input
                value={tagNumber}
                onChange={(e) => setTagNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Location</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g. Warehouse A, Shelf 3"
            />
          </div>
          <div>
            <Label className="text-xs">Inspection Template</Label>
            <SearchableSelect
              value={inspectionTemplateId}
              onChange={setInspectionTemplateId}
              allLabel="None"
              allValue=""
              options={templates.filter((t) => t.isActive).map((t) => ({ value: t.id, label: t.name }))}
            />
          </div>
          <div>
            <Label className="text-xs">Inspection Interval (days)</Label>
            <Input
              type="number"
              min="1"
              value={inspectionIntervalDays}
              onChange={(e) => setInspectionIntervalDays(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g. 30"
            />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Optional notes about this asset..."
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!name.trim() || isPending}
              onClick={handleSubmit}
            >
              {isPending
                ? isEdit
                  ? "Saving..."
                  : "Creating..."
                : isEdit
                  ? "Save"
                  : "Create"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// --------------- Asset Detail Sheet ---------------

interface AssignmentRecord {
  assignment: {
    id: string;
    assetId: string;
    userId: string;
    checkedOutAt: string;
    checkedInAt: string | null;
    conditionOut: string;
    conditionIn: string | null;
    checkInNotes: string | null;
  };
  userName: string;
}

const CONDITION_COLORS: Record<string, string> = {
  good: "text-green-600",
  fair: "text-yellow-600",
  poor: "text-red-600",
  damaged: "text-red-600",
  needs_service: "text-amber-600",
};

function AssetDetailSheet({
  asset,
  onClose,
  onEdit,
  onOosToggle,
}: {
  asset: Asset | null;
  onClose: () => void;
  onEdit: (asset: Asset) => void;
  onOosToggle: (asset: Asset) => void;
}) {
  const [showQr, setShowQr] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Fetch full detail (includes inspectionTemplateName)
  const { data: detailData } = useQuery({
    queryKey: ["admin", "assets", asset?.id],
    queryFn: () =>
      apiGet<{ data: Asset }>(`/api/admin/assets/${asset!.id}`),
    enabled: !!asset,
  });

  // Fetch assignment history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ["admin", "assets", asset?.id, "assignments"],
    queryFn: () =>
      apiGet<{ data: AssignmentRecord[]; total: number }>(
        `/api/assets/${asset!.id}/assignments?limit=10`
      ),
    enabled: !!asset && showHistory,
  });

  const detail = detailData?.data || asset;
  if (!detail) return null;

  return (
    <Sheet open={!!asset} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{detail.name}</SheetTitle>
          <SheetDescription>Asset Details</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          {/* Status and type */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">
              {ASSET_TYPE_LABELS[detail.assetType] || detail.assetType}
            </Badge>
            <Badge
              className={cn(
                "text-xs border-0",
                STATUS_COLORS[detail.status] || "bg-gray-100"
              )}
            >
              {STATUS_LABELS[detail.status] || detail.status}
            </Badge>
            {!detail.isActive && (
              <Badge variant="secondary" className="text-xs">
                Inactive
              </Badge>
            )}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {detail.serialNumber && (
              <div>
                <span className="text-xs text-muted-foreground block">Serial Number</span>
                <span>{detail.serialNumber}</span>
              </div>
            )}
            {detail.tagNumber && (
              <div>
                <span className="text-xs text-muted-foreground block">Tag Number</span>
                <span>{detail.tagNumber}</span>
              </div>
            )}
            {detail.location && (
              <div>
                <span className="text-xs text-muted-foreground block">Location</span>
                <span>{detail.location}</span>
              </div>
            )}
            {detail.inspectionTemplateName && (
              <div>
                <span className="text-xs text-muted-foreground block">
                  Inspection Template
                </span>
                <span>{detail.inspectionTemplateName}</span>
              </div>
            )}
            {detail.inspectionIntervalDays && (
              <div>
                <span className="text-xs text-muted-foreground block">
                  Inspection Interval
                </span>
                <span>{detail.inspectionIntervalDays} days</span>
              </div>
            )}
            {detail.lastInspectedAt && (
              <div>
                <span className="text-xs text-muted-foreground block">
                  Last Inspected
                </span>
                <span>
                  {new Date(detail.lastInspectedAt).toLocaleDateString()}
                </span>
              </div>
            )}
            {detail.nextInspectionDueAt && (
              <div>
                <span className="text-xs text-muted-foreground block">
                  Next Inspection Due
                </span>
                <span>
                  {new Date(detail.nextInspectionDueAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {detail.notes && (
            <div>
              <span className="text-xs text-muted-foreground block mb-1">Notes</span>
              <p className="text-sm">{detail.notes}</p>
            </div>
          )}

          {/* QR Code Section */}
          <div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowQr(!showQr)}
            >
              <QrCode className="h-4 w-4 mr-1" />
              {showQr ? "Hide" : "Show"} QR Label
              {showQr ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              )}
            </Button>
            {showQr && (
              <div className="mt-3">
                <AssetQRLabel asset={detail} />
              </div>
            )}
          </div>

          {/* Assignment History */}
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium">Assignment History</span>
              {showHistory ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {showHistory && (
              <div className="mt-3">
                {historyLoading ? (
                  <div className="space-y-2 py-2">
                    <div className="h-4 bg-muted rounded animate-pulse" />
                    <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                  </div>
                ) : historyData?.data && historyData.data.length > 0 ? (
                  <div className="border rounded-lg divide-y">
                    {historyData.data.map((record) => {
                      const a = record.assignment;
                      const isActive = !a.checkedInAt;
                      return (
                        <div key={a.id} className="px-3 py-2.5 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">
                              {record.userName}
                            </span>
                            {isActive ? (
                              <Badge className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0 border-0">
                                Checked Out
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground shrink-0">
                                {new Date(a.checkedInAt!).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span>
                              Out: {new Date(a.checkedOutAt).toLocaleDateString()}
                            </span>
                            <span className={CONDITION_COLORS[a.conditionOut] || ""}>
                              {a.conditionOut}
                            </span>
                            {a.conditionIn && (
                              <>
                                <span>&rarr;</span>
                                <span className={CONDITION_COLORS[a.conditionIn] || ""}>
                                  {a.conditionIn}
                                </span>
                              </>
                            )}
                          </div>
                          {a.checkInNotes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              {a.checkInNotes}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    No checkout history yet.
                  </p>
                )}
                {historyData && historyData.total > 10 && (
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Showing 10 of {historyData.total} records
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => onEdit(detail)}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
            {detail.status !== "retired" && (
              <Button
                size="sm"
                variant={detail.status === "out_of_service" ? "outline" : "destructive"}
                className={cn(
                  "flex-1",
                  detail.status === "out_of_service" &&
                    "border-green-500 text-green-700 hover:bg-green-50"
                )}
                onClick={() => onOosToggle(detail)}
              >
                {detail.status === "out_of_service" ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Clear OOS
                  </>
                ) : (
                  <>
                    <Ban className="h-4 w-4 mr-1" />
                    Mark OOS
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
